import { useMemo } from "react";
import type { HrisPtoTimeOff, HrisPtoTypeStats, PtoTypes, TimeOffReasons, TimeOffTypeMapping } from "../../../../../../utils/models";

/**
 * Row shape consumed by the LEFT pane (`HrisRawPtoTypes`) of the Time Off Pretty Mapping view — one row
 * per raw PTO event type, joining its resolved mapping with the last-60-day time-offs (DAT-200).
 *
 * `isUnmapped` rows are raw PTO types present in HRIS data with no mapping at all (no default, no override).
 * The right pane (`HrisTimeOffOverview`) does NOT use this — it consumes `TimeOffTypeMapping[]` directly.
 */
export type PtoTypeRow = {
	rawString: string;
	ptoType: PtoTypes | null;
	reason: TimeOffReasons | null;
	isOverride: boolean;
	isCustom: boolean;
	isUnmapped: boolean;
	defaultDisplay: string | null;
	count: number;
	timeOffs: HrisPtoTimeOff[];
};

const norm = (raw: string) => raw.trim().toLowerCase();

/**
 * Builds the row set rendered by the LEFT pane of the Time Off Pretty Mapping view.
 *
 * Mirrors the Raw Table grid's row logic:
 * 1. Custom rows are always kept; non-custom mapping rows must appear in the raw PTO codes.
 * 2. Raw PTO codes with no mapping at all are appended as synthetic `isUnmapped` rows (deduped by
 *    normalized key, matching the backend's exact-match DISTINCT).
 * Each row is enriched with its last-60-day count + time-offs, joined by normalized raw string.
 */
export function usePtoTypeRows(
	mappings: TimeOffTypeMapping[] | undefined,
	rawPtoTypes: string[] | undefined,
	stats: HrisPtoTypeStats[] | undefined,
): PtoTypeRow[] {
	return useMemo(() => {
		const statsByRaw = new Map((stats ?? []).map((s) => [s.rawString, s]));
		const rawPtoTypeSet = new Set((rawPtoTypes ?? []).map(norm).filter(Boolean));
		const mappingKeySet = new Set((mappings ?? []).map((m) => norm(m.rawString)));

		const enrich = (rawString: string, base: Omit<PtoTypeRow, "count" | "timeOffs">): PtoTypeRow => {
			const stat = statsByRaw.get(norm(rawString));
			return { ...base, count: stat?.countLast60 ?? 0, timeOffs: stat?.timeOffs ?? [] };
		};

		const mapped: PtoTypeRow[] = (mappings ?? [])
			.filter((m) => m.isCustom || rawPtoTypeSet.has(norm(m.rawString)))
			.map((m) =>
				enrich(m.rawString, {
					rawString: m.rawString,
					ptoType: m.ptoType,
					reason: m.reason ?? null,
					// Custom rows render with isOverride: false to hide the restore affordance (matches the grid).
					isOverride: m.isCustom ? false : m.isOverride,
					isCustom: m.isCustom,
					isUnmapped: false,
					defaultDisplay: m.defaultDisplay ?? null,
				}),
			);

		const synthetic: PtoTypeRow[] = [];
		const seen = new Set<string>();
		for (const code of rawPtoTypes ?? []) {
			const key = norm(code);
			if (!key || mappingKeySet.has(key) || seen.has(key)) continue;
			seen.add(key);
			synthetic.push(
				enrich(code, { rawString: code, ptoType: null, reason: null, isOverride: false, isCustom: false, isUnmapped: true, defaultDisplay: null }),
			);
		}

		return [...mapped, ...synthetic].sort((a, b) => a.rawString.localeCompare(b.rawString));
	}, [mappings, rawPtoTypes, stats]);
}
